import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  Package, Search, X,
  ExternalLink, Star, TrendingUp, Box, Eye, Pause, Play, DollarSign,
  Film,
} from 'lucide-react';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import ClipsTab from './tabs/ClipsTab';
import { useCan, pickActiveTab, Can, NoAccess, TAB_CAP } from '../../lib/rbac';

interface CatalogProduct {
  item_id: string;
  seller_id: number;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  asin: string;
  thumbnail: string;
  secure_thumbnail: string;
  available_quantity: number;
  sold_quantity: number;
  brand: string;
  model: string;
  manual_stock: boolean;
  markup_override: boolean;
  markup_type: string;
  markup_value: number;
  manual_pricing: boolean;
  automation_enabled: boolean | null;
  has_automation: boolean;
  seller_custom_field: string | null;
  item_amazon_price: number;
  date_created: string;
  last_updated: string;
  permalink: string;
  amazon_price: number | null;
  amazon_image: string | null;
  amazon_brand: string | null;
  amazon_category: string | null;
  amazon_available: boolean | null;
  amazon_rating: number | null;
  amazon_ratings_total: number | null;
  keepa_monthly_sold: number | null;
  keepa_availability_amazon: number | null;
  weight_kg: number | null;
  dimensions_cm: string | null;
  margin_pct: number | null;
  site_count: number;
  // Internal stock
  cost_source: string | null;
  internal_cost: number | null;
  stock_source_type: string | null;
  internal_sku: string | null;
  internal_qty_on_hand: number | null;
  supplier_name: string | null;
  // When loading detail
  site_items?: SiteItem[];
}

interface SiteItem {
  site_item_id: string;
  site_id: string;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  available_quantity: number;
  sold_quantity: number;
  net_proceeds: number | null;
  sale_fee: number | null;
  shipping_cost: number | null;
  permalink: string | null;
}

interface CatalogStats {
  total: number;
  active: number;
  paused: number;
  under_review: number;
  closed: number;
  total_stock: number;
  total_sold: number;
}



const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  active: 'ok',
  paused: 'warning',
  under_review: 'info',
  closed: 'error',
};

const SITE_FLAGS: Record<string, string> = {
  MLA: 'AR', MLM: 'MX', MLU: 'UY', MLC: 'CL', MCO: 'CO', MLB: 'BR',
};

/** Acciones masivas: cada una pide su propia capacidad. */
const BULK_ACTIONS: { value: string; label: string; cap: string }[] = [
  { value: 'pause', label: 'Pausar', cap: 'catalog.pause_activate' },
  { value: 'activate', label: 'Activar', cap: 'catalog.pause_activate' },
  { value: 'close', label: 'Cerrar', cap: 'catalog.close_delete' },
  { value: 'delete', label: 'Eliminar', cap: 'catalog.close_delete' },
  { value: 'reprice', label: 'Reprice', cap: 'catalog.edit_price' },
  { value: 'toggle_pm_off', label: 'PM OFF', cap: 'catalog.toggles' },
  { value: 'toggle_pm_on', label: 'PM ON', cap: 'catalog.toggles' },
];

export default function CatalogPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [activeTab, setActiveTab] = useState<'productos' | 'clips'>('productos');
  // Pestaña efectiva: la elegida si está permitida; si no, la primera permitida.
  const active = pickActiveTab(
    'catalog',
    [{ key: 'productos' }, { key: 'clips' }],
    activeTab,
    can,
  ) as 'productos' | 'clips' | null;
  useEffect(() => {
    if (active && active !== activeTab) setActiveTab(active);
  }, [active, activeTab]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const pag = usePagination('catalog');
  const { sellerId } = useUIStore();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<CatalogProduct | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  // Extended filters
  const [costSourceFilter, setCostSourceFilter] = useState('all');
  const [controlsFilter, setControlsFilter] = useState<string[]>([]);
  const [siteFilter, setSiteFilter] = useState('all');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [hasAsinFilter, setHasAsinFilter] = useState('all');
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  // Inline reprice
  const [repricingId, setRepricingId] = useState<string | null>(null);
  const [repriceValue, setRepriceValue] = useState('');
  // Reviews badge map: item_id -> { rating_average, total_reviews }
  const [reviewsMap, setReviewsMap] = useState<Record<string, { rating: number; total: number }>>({});


  // Load stats
  const loadStats = useCallback(async () => {
    const res = await api.get<CatalogStats>(`/catalog/stats?seller_id=${sellerId}`);
    if (res.success && res.data) setStats(res.data);
  }, [sellerId]);

  // Load products
  const loadProducts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      seller_id: String(sellerId),
      page: String(pag.page),
      per_page: String(pag.perPage),
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (search) params.set('search', search);
    if (costSourceFilter !== 'all') params.set('cost_source', costSourceFilter);
    if (controlsFilter.length > 0) params.set('controls', controlsFilter.join(','));
    if (siteFilter !== 'all') params.set('site', siteFilter);
    if (priceMin) params.set('price_min', priceMin);
    if (priceMax) params.set('price_max', priceMax);
    if (hasAsinFilter === 'con_asin') params.set('has_asin', 'true');
    if (hasAsinFilter === 'sin_asin') params.set('has_asin', 'false');

    const res = await api.get<CatalogProduct[]>(`/catalog/products?${params}`);
    if (res.success && res.data) {
      setProducts(res.data);
      pag.setTotal((res as any).meta?.total || 0);
    }
    setLoading(false);
  }, [sellerId, pag.page, pag.perPage, sortBy, sortDir, statusFilter, search, costSourceFilter, controlsFilter, siteFilter, priceMin, priceMax, hasAsinFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { pag.resetPage(); }, [sellerId]);

  // Load reviews summary (defensive — never break catalog if it fails)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<Array<{ item_id: string; rating_average: number; total_reviews: number }>>(
          `/reviews/seller/${sellerId}/summary?limit=2000&order=recent`
        );
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          const map: Record<string, { rating: number; total: number }> = {};
          for (const r of res.data) {
            map[r.item_id] = { rating: Number(r.rating_average) || 0, total: Number(r.total_reviews) || 0 };
          }
          setReviewsMap(map);
        }
      } catch {
        // silenciar — no romper el catálogo si reviews falla
      }
    })();
    return () => { cancelled = true; };
  }, [sellerId]);

  // SSE real-time updates
  const { subscribe } = useSSEStore();
  const loadProductsRef = useRef(loadProducts);
  const loadStatsRef = useRef(loadStats);
  loadProductsRef.current = loadProducts;
  loadStatsRef.current = loadStats;
  useEffect(() => {
    const refresh = () => { loadProductsRef.current(); loadStatsRef.current(); };
    const unsubs = [
      subscribe('stock:updated', refresh),
      subscribe('stock:adjusted', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  // Debounced search
  const handleSearch = (val: string) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setSearch(val);
      pag.resetPage();
    }, 400);
    setSearchTimeout(timeout);
  };

  // Load product detail
  const openDetail = async (product: CatalogProduct) => {
    setSelectedProduct(product);
    setDetailLoading(true);
    const res = await api.get<CatalogProduct & { site_items: SiteItem[] }>(
      `/catalog/products/${product.item_id}?seller_id=${sellerId}`
    );
    if (res.success && res.data) {
      setSelectedProduct(res.data);
    }
    setDetailLoading(false);
  };

  // Bulk actions
  const executeBulk = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    if (['close', 'delete', 'reprice', 'toggle_pm_off'].includes(bulkAction)) {
      if (!confirm(`¿Seguro? ${bulkAction === 'delete' ? 'ELIMINAR PERMANENTEMENTE' : bulkAction} en ${selectedIds.size} items`)) return;
    }
    setBulkLoading(true);
    const body: Record<string, unknown> = { action: bulkAction, item_ids: Array.from(selectedIds) };
    if (bulkAction === 'reprice' && bulkPrice) body.price = Number(bulkPrice);
    await api.post(`/catalog/bulk?seller_id=${sellerId}`, body);
    setSelectedIds(new Set());
    setBulkAction('');
    setBulkPrice('');
    setBulkLoading(false);
    loadProducts();
    loadStats();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.item_id)));
    }
  };

  const inlineReprice = async (itemId: string) => {
    if (!repriceValue || Number(repriceValue) <= 0) return;
    await api.put(`/catalog/products/${itemId}/ml/price?seller_id=${sellerId}`, { price: Number(repriceValue) });
    setRepricingId(null);
    setRepriceValue('');
    loadProducts();
  };

  const quickToggleStatus = async (e: React.MouseEvent, itemId: string, currentStatus: string) => {
    e.stopPropagation();
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    await api.put(`/catalog/products/${itemId}/ml/status?seller_id=${sellerId}`, { status: newStatus });
    loadProducts();
    loadStats();
  };

  const productImage = (p: CatalogProduct) => p.secure_thumbnail || p.thumbnail || p.amazon_image || '';

  const formatPrice = (v: number | null | undefined) => v != null ? `$${Number(v).toFixed(2)}` : '—';

  // Sin la llave del dinero: ni precio de Amazon ni margen.
  const canMoney = can('global.view_money');
  const bulkActions = BULK_ACTIONS.filter(a => can(a.cap));
  const canBulk = bulkActions.length > 0;

  const marginColor = (m: number | null) => {
    if (m == null) return 'text-[var(--color-text-muted)]';
    if (m >= 30) return 'text-[var(--color-success)]';
    if (m >= 15) return 'text-[var(--color-warning)]';
    return 'text-[var(--color-error)]';
  };

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">{t('catalog.title')}</h1>
      </div>

      {/* Tabs strip */}
      <div className="flex border-b border-[var(--color-border)]">
        {[
          { key: 'productos', label: 'Productos', icon: Package },
          { key: 'clips', label: 'Clips', icon: Film },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <Can key={tab.key} cap={TAB_CAP.catalog[tab.key]}>
              <button
                onClick={() => setActiveTab(tab.key as 'productos' | 'clips')}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active === tab.key
                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                    : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            </Can>
          );
        })}
      </div>

      {active === 'clips' && <ClipsTab />}

      {active === 'productos' && (<>
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: t('catalog.totalProducts'), value: stats.total.toLocaleString(), color: 'var(--color-primary)' },
            { label: t('catalog.active'), value: stats.active.toLocaleString(), color: 'var(--color-success)' },
            { label: t('catalog.paused'), value: stats.paused.toLocaleString(), color: 'var(--color-warning)' },
            { label: t('catalog.underReview'), value: stats.under_review.toLocaleString(), color: 'var(--color-primary)' },
            { label: t('catalog.closed'), value: stats.closed.toLocaleString(), color: 'var(--color-error)' },
            { label: t('catalog.totalStock'), value: stats.total_stock.toLocaleString(), color: 'var(--color-text)' },
            { label: t('catalog.totalSold'), value: stats.total_sold.toLocaleString(), color: 'var(--color-text)' },
          ].map((s, i) => (
            <div key={i} className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 text-center">
              <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder={t('catalog.searchPlaceholder')}
            defaultValue={search}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
        >
          <option value="all">{t('catalog.allProducts')}</option>
          <option value="active">{t('catalog.active')}</option>
          <option value="paused">{t('catalog.paused')}</option>
          <option value="under_review">{t('catalog.underReview')}</option>
          <option value="closed">{t('catalog.closed')}</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
        >
          <option value="last_updated">{t('catalog.updated')}</option>
          <option value="title">{t('catalog.product')}</option>
          <option value="price">{t('catalog.mlPrice')}</option>
          <option value="amazon_price">{t('catalog.amazonPrice')}</option>
          <option value="stock">{t('catalog.stock')}</option>
          <option value="sold">{t('catalog.sold')}</option>
          <option value="margin">{t('catalog.margin')}</option>
        </select>
        <button
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
        >
          {sortDir === 'asc' ? '↑' : '↓'}
        </button>
      </div>

      {/* Extended filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={costSourceFilter} onChange={e => { setCostSourceFilter(e.target.value); pag.resetPage(); }}
          className="px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]">
          <option value="all">Fuente: Todas</option>
          <option value="amazon">Amazon</option>
          <option value="internal_stock">Stock Interno</option>
          <option value="manual">Manual</option>
        </select>
        <select value={siteFilter} onChange={e => { setSiteFilter(e.target.value); pag.resetPage(); }}
          className="px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]">
          <option value="all">Site: Todos</option>
          <option value="MLA">MLA</option><option value="MLB">MLB</option>
          <option value="MLM">MLM</option><option value="MLC">MLC</option><option value="MCO">MCO</option>
        </select>
        <select value={hasAsinFilter} onChange={e => { setHasAsinFilter(e.target.value); pag.resetPage(); }}
          className="px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]">
          <option value="all">ASIN: Todos</option>
          <option value="con_asin">Con ASIN</option>
          <option value="sin_asin">Sin ASIN</option>
        </select>
        <div className="flex items-center gap-1">
          <input type="number" placeholder="Min $" value={priceMin}
            onChange={e => setPriceMin(e.target.value)}
            onBlur={() => { pag.resetPage(); loadProducts(); }}
            className="w-20 px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]" />
          <span className="text-xs text-[var(--color-text-muted)]">-</span>
          <input type="number" placeholder="Max $" value={priceMax}
            onChange={e => setPriceMax(e.target.value)}
            onBlur={() => { pag.resetPage(); loadProducts(); }}
            className="w-20 px-2 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]" />
        </div>
        {['markup_override', 'manual_stock', 'manual_pricing', 'automation_active', 'automation_enabled', 'automation_blocked'].map(ctrl => (
          <button key={ctrl} onClick={() => {
            setControlsFilter(prev => prev.includes(ctrl) ? prev.filter(c => c !== ctrl) : [...prev, ctrl]);
            pag.resetPage();
          }}
            className={`px-2 py-1 rounded-full text-xs border transition-colors ${
              controlsFilter.includes(ctrl)
                ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-secondary)]'
            }`}>
            {ctrl.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && canBulk && (
        <div className="flex items-center gap-3 bg-[var(--color-surface)] border border-[var(--color-primary)] rounded-lg px-4 py-2">
          <span className="text-sm font-medium text-[var(--color-text)]">{selectedIds.size} seleccionados</span>
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
            className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]">
            <option value="">Accion...</option>
            {bulkActions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          {bulkAction === 'reprice' && (
            <input type="number" placeholder="Precio USD" value={bulkPrice}
              onChange={e => setBulkPrice(e.target.value)}
              className="w-24 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]" />
          )}
          <button onClick={executeBulk} disabled={!bulkAction || bulkLoading}
            className="px-3 py-1 rounded bg-[var(--color-primary)] text-white text-sm disabled:opacity-50">
            {bulkLoading ? 'Procesando...' : 'Ejecutar'}
          </button>
          <button onClick={() => { setSelectedIds(new Set()); setBulkAction(''); }}
            className="px-3 py-1 rounded border border-[var(--color-border)] text-sm text-[var(--color-text)]">Cancelar</button>
        </div>
      )}

      {/* Product table */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="animate-pulse p-4 space-y-3">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="h-16 bg-[var(--color-border-light)] rounded" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <Package size={48} className="opacity-20 mb-3" />
            <p>{t('catalog.noProducts')}</p>
          </div>
        ) : isMobile ? (
          /* Mobile card view */
          <div className="space-y-2 p-2">
            {products.map(p => (
              <div
                key={p.item_id}
                onClick={() => navigate(`/catalog/${p.item_id}`)}
                className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3 space-y-2 cursor-pointer active:bg-[var(--color-surface-hover)]"
              >
                <div className="flex gap-2.5">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--color-border-light)] shrink-0">
                    {productImage(p) ? (
                      <img src={productImage(p)} alt="" className="w-full h-full object-contain" loading="lazy" />
                    ) : (
                      <Package size={16} className="w-full h-full p-3 text-[var(--color-text-muted)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--color-text)] line-clamp-1">{p.title || '—'}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-[var(--color-text-muted)]">
                      {p.asin && <span className="font-mono">{p.asin}</span>}
                      {p.brand && <><span>·</span><span>{p.brand}</span></>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <StatusBadge status={STATUS_MAP[p.status] || 'unknown'} label={p.status} size="sm" />
                  <span className="font-mono font-medium text-[var(--color-text)]">{formatPrice(p.price)}</span>
                  {canMoney && p.amazon_price && (
                    <span className="font-mono text-[var(--color-text-muted)]">AMZ {formatPrice(p.amazon_price)}</span>
                  )}
                  {canMoney && p.margin_pct != null && (
                    <span className={`ml-auto font-medium ${p.margin_pct >= 20 ? 'text-[var(--color-success)]' : p.margin_pct >= 0 ? 'text-[var(--color-warning)]' : 'text-[var(--color-error)]'}`}>
                      {p.margin_pct.toFixed(0)}%
                    </span>
                  )}
                  {p.site_count > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                      {p.site_count} sites
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-hover)]">
                  <th className="px-2 py-2.5 w-8">
                    {canBulk && (
                      <input type="checkbox" checked={selectedIds.size === products.length && products.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-[var(--color-border)]" />
                    )}
                  </th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-14">Img</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-3 py-2.5">Producto</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-28">ASIN</th>
                  <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-28">SKU</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-20">ML $</th>
                  <th className="text-right text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-20">AMZ $</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-24">{t('common.status')}</th>
                  <th className="text-center text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider px-2 py-2.5 w-36">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr
                    key={p.item_id}
                    className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)] transition-colors cursor-pointer"
                    onClick={() => navigate(`/catalog/${p.item_id}`)}
                  >
                    {/* Checkbox */}
                    <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                      {canBulk && (
                        <input type="checkbox" checked={selectedIds.has(p.item_id)}
                          onChange={() => toggleSelect(p.item_id)}
                          className="rounded border-[var(--color-border)]" />
                      )}
                    </td>
                    {/* Image */}
                    <td className="px-2 py-2">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-[var(--color-border-light)] flex-shrink-0">
                        {productImage(p) ? (
                          <img src={productImage(p)} alt="" className="w-full h-full object-contain" loading="lazy" />
                        ) : (
                          <Package size={16} className="w-full h-full p-2 text-[var(--color-text-muted)]" />
                        )}
                      </div>
                    </td>
                    {/* Title + Brand */}
                    <td className="px-3 py-2 max-w-xs">
                      <div className="text-sm font-medium text-[var(--color-text)] line-clamp-1">{p.title || '\u2014'}</div>
                      <div className="text-xs text-[var(--color-text-muted)] mt-0.5 flex items-center gap-2">
                        {p.brand && <span>{p.brand}</span>}
                        {p.cost_source === 'internal_stock' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                            Stock Interno
                          </span>
                        )}
                        {reviewsMap[p.item_id]?.total > 0 ? (
                          <span
                            className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${
                              reviewsMap[p.item_id].rating < 3 ? 'text-red-600' :
                              reviewsMap[p.item_id].rating < 4 ? 'text-yellow-600' : 'text-green-600'
                            }`}
                            title={`${reviewsMap[p.item_id].rating.toFixed(1)} sobre 5 \u00b7 ${reviewsMap[p.item_id].total} rese\u00f1as`}
                          >
                            <Star size={11} className="fill-current" />
                            {reviewsMap[p.item_id].rating.toFixed(1)} ({reviewsMap[p.item_id].total})
                          </span>
                        ) : reviewsMap[p.item_id] ? (
                          <span className="text-[10px] text-[var(--color-text-muted)] italic">Sin rese\u00f1as</span>
                        ) : null}
                      </div>
                    </td>
                    {/* ASIN */}
                    <td className="px-2 py-2 text-xs font-mono text-[var(--color-text-muted)]">
                      {p.asin || '\u2014'}
                    </td>
                    {/* SKU Seller */}
                    <td className="px-2 py-2 text-xs font-mono text-[var(--color-text-muted)]">
                      {p.seller_custom_field || '\u2014'}
                    </td>
                    {/* ML price */}
                    <td className="px-2 py-2 text-right text-sm font-mono font-medium">
                      {formatPrice(p.price)}
                    </td>
                    {/* Amazon price */}
                    <td className="px-2 py-2 text-right text-sm font-mono text-[var(--color-text-muted)]">
                      {canMoney ? formatPrice(p.amazon_price ?? p.item_amazon_price) : '—'}
                    </td>
                    {/* Status */}
                    <td className="px-2 py-2 text-center">
                      <StatusBadge
                        status={STATUS_MAP[p.status] || 'unknown'}
                        label={t(`catalog.${p.status === 'under_review' ? 'underReview' : p.status}`) || p.status}
                        size="sm"
                      />
                    </td>
                    {/* Acciones */}
                    <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                      {repricingId === p.item_id ? (
                        <div className="flex items-center gap-1">
                          <input type="number" value={repriceValue} onChange={e => setRepriceValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') inlineReprice(p.item_id); if (e.key === 'Escape') setRepricingId(null); }}
                            placeholder="USD" autoFocus
                            className="w-20 px-1.5 py-1 rounded border border-[var(--color-primary)] bg-[var(--color-surface)] text-xs text-[var(--color-text)]" />
                          <button onClick={() => inlineReprice(p.item_id)}
                            className="p-1 rounded bg-[var(--color-primary)] text-white text-xs">OK</button>
                          <button onClick={() => setRepricingId(null)}
                            className="p-1 rounded text-xs text-[var(--color-text-muted)]">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-0.5">
                          <button onClick={() => navigate(`/catalog/${p.item_id}`)}
                            title="Ver detalle"
                            className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]">
                            <Eye size={14} className="text-[var(--color-text-muted)]" />
                          </button>
                          <Can cap="catalog.pause_activate">
                            <button onClick={e => quickToggleStatus(e, p.item_id, p.status)}
                              title={p.status === 'active' ? 'Pausar' : 'Activar'}
                              className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]">
                              {p.status === 'active'
                                ? <Pause size={14} className="text-[var(--color-warning)]" />
                                : <Play size={14} className="text-[var(--color-success)]" />}
                            </button>
                          </Can>
                          {p.permalink && (
                            <a href={p.permalink} target="_blank" rel="noopener noreferrer"
                              title="Ver en ML"
                              className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]">
                              <ExternalLink size={14} className="text-[var(--color-primary)]" />
                            </a>
                          )}
                          {p.asin && (
                            <a href={`https://www.amazon.com/dp/${p.asin}`} target="_blank" rel="noopener noreferrer"
                              title="Ver en Amazon"
                              className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]">
                              <ExternalLink size={14} className="text-orange-500" />
                            </a>
                          )}
                          <Can cap="catalog.edit_price">
                            <button onClick={() => { setRepricingId(p.item_id); setRepriceValue(p.price ? String(p.price) : ''); }}
                              title="Reprice"
                              className="p-1.5 rounded hover:bg-[var(--color-surface-hover)]">
                              <DollarSign size={14} className="text-[var(--color-text-muted)]" />
                            </button>
                          </Can>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pag.total > 0 && (
          <div className="px-4 py-3 border-t border-[var(--color-border)]">
            <Pagination
              page={pag.page}
              totalPages={pag.totalPages}
              total={pag.total}
              perPage={pag.perPage}
              onPageChange={pag.setPage}
              onPerPageChange={pag.setPerPage}
            />
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedProduct && (
        <ProductDetailPanel
          product={selectedProduct}
          loading={detailLoading}
          onClose={() => setSelectedProduct(null)}
        />
      )}
      </>)}
    </div>
  );
}

// ── Product Detail Slide Panel ──

function ProductDetailPanel({
  product,
  loading,
  onClose,
}: {
  product: CatalogProduct;
  loading: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const img = product.secure_thumbnail || product.thumbnail || product.amazon_image || '';
  const amzPrice = product.amazon_price ?? product.item_amazon_price;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-[var(--color-surface)] shadow-2xl z-50 overflow-y-auto animate-slide-in">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[var(--color-text)]">{t('catalog.detail')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)]">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Product header */}
          <div className="flex gap-4">
            <div className="w-32 h-32 rounded-xl overflow-hidden bg-[var(--color-border-light)] flex-shrink-0 border border-[var(--color-border)]">
              {img ? (
                <img src={img} alt="" className="w-full h-full object-contain p-1" />
              ) : (
                <Package size={40} className="w-full h-full p-8 text-[var(--color-text-muted)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[var(--color-text)] line-clamp-3">{product.title || '—'}</h3>
              <div className="mt-2 space-y-1 text-xs text-[var(--color-text-muted)]">
                {product.asin && (
                  <div>
                    <span className="text-[var(--color-text-secondary)]">ASIN:</span>{' '}
                    <span className="font-mono">{product.asin}</span>
                  </div>
                )}
                {product.brand && (
                  <div>
                    <span className="text-[var(--color-text-secondary)]">{t('catalog.brand')}:</span> {product.brand}
                  </div>
                )}
                {product.model && (
                  <div>
                    <span className="text-[var(--color-text-secondary)]">{t('catalog.model')}:</span> {product.model}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <StatusBadge
                  status={STATUS_MAP[product.status] || 'unknown'}
                  label={product.status}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Pricing section */}
          <Section title={t('catalog.pricing')}>
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label={t('catalog.amazonPrice')} value={canMoney && amzPrice ? `$${Number(amzPrice).toFixed(2)}` : '—'} />
              <InfoCard label={t('catalog.mlPrice')} value={product.price ? `$${Number(product.price).toFixed(2)}` : '—'} highlight />
              <InfoCard
                label={t('catalog.margin')}
                value={canMoney && product.margin_pct != null ? `${product.margin_pct}%` : '—'}
                valueColor={
                  !canMoney || product.margin_pct == null ? undefined :
                  product.margin_pct >= 30 ? 'var(--color-success)' :
                  product.margin_pct >= 15 ? 'var(--color-warning)' : 'var(--color-error)'
                }
              />
              <InfoCard
                label={t('catalog.markup')}
                value={
                  !canMoney ? '—'
                    : product.markup_override
                    ? `${product.markup_value}${product.markup_type === 'percentage' ? '%' : ' USD'} (custom)`
                    : 'Global'
                }
              />
            </div>
          </Section>

          {/* Stock section */}
          <Section title={t('catalog.stock')}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <InfoCard label={t('catalog.stock')} value={String(product.available_quantity ?? 0)} />
              <InfoCard label={t('catalog.sold')} value={(product.sold_quantity ?? 0).toLocaleString()} />
              <InfoCard
                label={t('catalog.manualStock')}
                value={product.manual_stock ? t('common.yes') : t('common.no')}
              />
            </div>
          </Section>

          {/* Site items */}
          <Section title={t('catalog.siteItems')}>
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[1,2,3].map(i => <div key={i} className="h-10 bg-[var(--color-border-light)] rounded" />)}
              </div>
            ) : product.site_items && product.site_items.length > 0 ? (
              <div className="space-y-1.5">
                {product.site_items.map(si => (
                  <div
                    key={si.site_item_id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border-light)]"
                  >
                    <span className="text-xs font-bold w-8 text-[var(--color-primary)]">
                      {SITE_FLAGS[si.site_id] || si.site_id}
                    </span>
                    <span className="text-sm font-mono flex-shrink-0">
                      {si.price ? `${si.currency_id} ${Number(si.price).toFixed(2)}` : '—'}
                    </span>
                    <StatusBadge
                      status={STATUS_MAP[si.status] || 'unknown'}
                      label={si.status}
                      size="sm"
                    />
                    <span className="text-xs text-[var(--color-text-muted)] ml-auto">
                      {si.sold_quantity || 0} {t('catalog.sold').toLowerCase()}
                    </span>
                    {canMoney && si.net_proceeds != null && (
                      <span className="text-xs text-[var(--color-text-muted)]">
                        NP: ${Number(si.net_proceeds).toFixed(2)}
                      </span>
                    )}
                    {si.permalink && (
                      <a
                        href={si.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[var(--color-primary)] hover:opacity-70"
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)] text-center py-4">{t('catalog.noSiteItems')}</p>
            )}
          </Section>

          {/* Amazon data */}
          {product.asin && (
            <Section title={t('catalog.amazonData')}>
              <div className="grid grid-cols-2 gap-3">
                {product.amazon_rating != null && (
                  <InfoCard
                    label={t('catalog.rating')}
                    value={`${product.amazon_rating} / 5`}
                    icon={<Star size={14} className="text-yellow-500 fill-yellow-500" />}
                    sub={product.amazon_ratings_total ? `${product.amazon_ratings_total.toLocaleString()} ${t('catalog.reviews')}` : undefined}
                  />
                )}
                {product.keepa_monthly_sold != null && (
                  <InfoCard
                    label={t('catalog.monthlySold')}
                    value={product.keepa_monthly_sold.toLocaleString()}
                    icon={<TrendingUp size={14} className="text-[var(--color-success)]" />}
                  />
                )}
                {product.weight_kg != null && (
                  <InfoCard label={t('catalog.weight')} value={`${product.weight_kg} kg`} />
                )}
                {product.dimensions_cm && (
                  <InfoCard label={t('catalog.dimensions')} value={product.dimensions_cm} />
                )}
                <InfoCard
                  label={t('catalog.available')}
                  value={
                    product.amazon_available === true ? t('catalog.available') :
                    product.amazon_available === false ? t('catalog.unavailable') : '—'
                  }
                  valueColor={product.amazon_available === true ? 'var(--color-success)' : product.amazon_available === false ? 'var(--color-error)' : undefined}
                />
                {product.amazon_category && (
                  <InfoCard label={t('catalog.category')} value={product.amazon_category} />
                )}
              </div>
            </Section>
          )}

          {/* Dates */}
          <Section title={t('common.date')}>
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label={t('catalog.created')} value={product.date_created ? new Date(product.date_created).toLocaleDateString() : '—'} />
              <InfoCard label={t('catalog.updated')} value={product.last_updated ? new Date(product.last_updated).toLocaleDateString() : '—'} />
            </div>
          </Section>

          {/* Links */}
          <div className="flex gap-3">
            {product.permalink && (
              <a
                href={product.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90"
              >
                <ExternalLink size={14} /> {t('catalog.viewOnML')}
              </a>
            )}
            {product.asin && (
              <a
                href={`https://www.amazon.com/dp/${product.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] text-sm font-medium hover:bg-[var(--color-surface-hover)]"
              >
                <ExternalLink size={14} /> {t('catalog.viewOnAmazon')}
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Helpers ──

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] font-semibold mb-2">{title}</h4>
      {children}
    </div>
  );
}

function InfoCard({
  label, value, highlight, valueColor, icon, sub,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  valueColor?: string;
  icon?: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className={`px-3 py-2 rounded-lg ${highlight ? 'bg-[var(--color-primary-light)]' : 'bg-[var(--color-bg)]'} border border-[var(--color-border-light)]`}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">{label}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {icon}
        <span
          className={`text-sm font-semibold ${highlight ? 'text-[var(--color-primary)]' : ''}`}
          style={valueColor ? { color: valueColor } : undefined}
        >
          {value}
        </span>
      </div>
      {sub && <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}
